import { Flex, Text } from '@chakra-ui/react';
import { memo, useEffect, useState } from 'react';

interface Props {
  endDate: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(endDate: string): TimeLeft {
  const diff = Math.max(0, new Date(endDate).getTime() - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

const TimeUnit = ({ value, label }: { value: number; label: string }) => (
  <Flex direction="column" alignItems="center" minW="32px">
    <Text fontSize="lg" fontWeight="bold" color="text.primary" fontVariantNumeric="tabular-nums">
      {String(value).padStart(2, '0')}
    </Text>
    <Text fontSize="2xs" color="text.secondary" textTransform="uppercase">
      {label}
    </Text>
  </Flex>
);

const CountdownTimer = ({ endDate }: Props) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft(endDate));

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(calcTimeLeft(endDate)), 1000);
    return () => clearInterval(id);
  }, [endDate]);

  const isExpired =
    timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  if (isExpired) {
    return (
      <Text fontSize="sm" fontWeight="semibold" color="error.500">
        Ended
      </Text>
    );
  }

  const showDays = timeLeft.days > 0;

  return (
    <Flex gap={1} alignItems="center">
      {showDays && (
        <>
          <TimeUnit value={timeLeft.days} label="Days" />
          <Text fontWeight="bold" color="text.secondary" pb={3}>:</Text>
        </>
      )}
      <TimeUnit value={timeLeft.hours} label={showDays ? 'Hrs' : 'Hours'} />
      <Text fontWeight="bold" color="text.secondary" pb={3}>:</Text>
      <TimeUnit value={timeLeft.minutes} label="Mins" />
      {!showDays && (
        <>
          <Text fontWeight="bold" color="text.secondary" pb={3}>:</Text>
          <TimeUnit value={timeLeft.seconds} label="Secs" />
        </>
      )}
    </Flex>
  );
};

export default memo(CountdownTimer);
